/**
 * PostgreSQL Backend Service
 *
 * Handles roles, plans, templates, and registration codes via the backend API.
 * Uses apiClient for consistent request handling and response unwrapping.
 */

import apiClient from './apiClient';

class PostgresBackendService {
  // ===== ROLE OPERATIONS =====

  /**
   * Get all roles for the current user (with tools)
   * @returns {Promise<Array>} List of roles with tools
   */
  async getRoles() {
    const params = new URLSearchParams();
    params.append('includeTools', 'true');
    return apiClient.get(`/api/roles?${params.toString()}`);
  }

  /**
   * Get roles by user ID
   * @deprecated userId is now automatically obtained from JWT token
   */
  async getRolesByUser(userId) {
    return this.getRoles();
  }

  /**
   * Get all roles without tools (faster)
   * @returns {Promise<Array>} List of roles
   */
  async getAllRoles({ includeDraft } = {}) {
    const qs = includeDraft ? '?includeDraft=true' : '';
    return apiClient.get(`/api/roles${qs}`);
  }

  /**
   * Get a role by ID (includes tools and files)
   * @param {string} id - Role ID
   * @returns {Promise<object>} Role with tools and files
   */
  async getRolById(id) {
    const rol = await apiClient.get(`/api/roles/${id}`);

    // Get tools and files separately
    const [tools, files] = await Promise.all([
      this.getRolTools(id).catch(() => []),
      this.getRolFiles(id).catch(() => [])
    ]);

    return {
      ...rol,
      tools,
      files
    };
  }

  /**
   * Get a role with draft fields. Used ONLY by Role Builder.
   * Production paths must keep using getRolById() — they must never see draft data.
   * @param {string} id - Role ID
   * @returns {Promise<object>} Role + hasDraft + draft_* fields + tools + files
   */
  async getRolWithDraft(id) {
    const rol = await apiClient.get(`/api/roles/${id}?includeDraft=true`);
    const [tools, files] = await Promise.all([
      this.getRolTools(id).catch(() => []),
      this.getRolFiles(id).catch(() => [])
    ]);
    return {
      ...rol,
      tools,
      files
    };
  }

  /**
   * Get tools for a role
   * @param {string} rolId - Role ID
   * @returns {Promise<Array>} List of tools
   */
  async getRolTools(rolId) {
    return apiClient.get(`/api/roles/${rolId}/tools`);
  }

  async getRolActionCards(rolId) {
    return apiClient.get(`/api/roles/${rolId}/action-cards`);
  }

  async setRolActionCards(rolId, cards) {
    return apiClient.put(`/api/roles/${rolId}/action-cards`, { cards });
  }

  /**
   * Get files for a role
   * @param {string} rolId - Role ID
   * @returns {Promise<Array>} List of files
   */
  async getRolFiles(rolId) {
    return apiClient.get(`/api/roles/${rolId}/files`);
  }

  /**
   * Get files for a role with fresh signed URLs
   * @param {string} rolId - Role ID
   * @returns {Promise<Array>} List of files with fresh S3 signed URLs
   */
  async getRolFilesWithUrls(rolId) {
    return apiClient.get(`/api/roles/${rolId}/files`);
  }

  /**
   * Get all available tools, optionally filtered by type.
   * Each tool in the response carries `types: string[]` (values from 'copiloto'|'experto').
   * A tool may belong to several types simultaneously.
   * @param {string|null} toolType - Optional single-value filter: 'copiloto' or 'experto'.
   *   The backend returns any tool whose `types` array contains this value.
   * @returns {Promise<Array>} List of tools with shape { id, name, description, types, configuration, ... }
   */
  async getAllTools(toolType = null) {
    const params = toolType ? `?toolType=${encodeURIComponent(toolType)}` : '';
    return apiClient.get(`/api/tools${params}`);
  }

  /**
   * Create a new role
   * @param {object} rolData - Role data
   * @returns {Promise<object>} Created role
   */
  async createRol(rolData) {
    return apiClient.post('/api/roles', rolData);
  }

  /**
   * Update an existing role
   * @param {string} id - Role ID
   * @param {object} rolData - Updated role data
   * @returns {Promise<object>} Updated role
   */
  async updateRol(id, rolData) {
    return apiClient.put(`/api/roles/${id}`, rolData);
  }

  // ===== DRAFT / PUBLISH =====

  /**
   * Save a draft of a role. Writes to draft_* columns only — never affects live agent.
   * Partial updates supported: only the fields present in `draftData` are written.
   * Un solo campo de prompt para todos los agentes (#1330): siempre `generalPrompt`.
   * `userInstructions` es la capa gestionada; el backend la rechaza con 403
   * MANAGED_LAYER_NOT_EDITABLE desde cualquier ruta de usuario.
   * @param {string} rolId - Role ID
   * @param {object} draftData - Partial draft fields (name, objectives, generalPrompt, color, tools)
   * @returns {Promise<object>} Updated role with draft fields populated
   */
  async saveDraft(rolId, draftData) {
    return apiClient.put(`/api/roles/${rolId}/draft`, draftData);
  }

  /**
   * Compute which plans will break if the current draft is published.
   * Diffs draft_tools vs live rol_tool and walks plan_data_pure JSONB.
   * @param {string} rolId - Role ID
   * @returns {Promise<{removedTools: Array, impactedPlans: Array}>}
   */
  async getDraftImpact(rolId) {
    return apiClient.get(`/api/roles/${rolId}/draft-impact`);
  }

  /**
   * Publish the draft to live. Creates an automatic version snapshot.
   * @param {string} rolId - Role ID
   * @returns {Promise<object>} Live role + versionNumber + versionId
   */
  async publishDraft(rolId) {
    return apiClient.post(`/api/roles/${rolId}/publish`);
  }

  /**
   * Discard the draft and restore the live version.
   * @param {string} rolId - Role ID
   * @returns {Promise<object>} Live role
   */
  async discardDraft(rolId) {
    return apiClient.post(`/api/roles/${rolId}/discard-draft`);
  }

  // ===== VERSION HISTORY =====

  /**
   * List version history for a role (summaries only).
   * @param {string} rolId - Role ID
   * @returns {Promise<Array>} Array of { id, versionNumber, name, publishedAt }
   */
  async getVersions(rolId) {
    return apiClient.get(`/api/roles/${rolId}/versions`);
  }

  /**
   * Get full snapshot of a single version.
   * @param {string} rolId - Role ID
   * @param {string} versionId - Version ID
   * @returns {Promise<object>} Version with full snapshot JSONB
   */
  async getVersion(rolId, versionId) {
    return apiClient.get(`/api/roles/${rolId}/versions/${versionId}`);
  }

  /**
   * Restore a version into the role's draft (does NOT publish).
   * User can then preview and publish to make it live.
   * @param {string} rolId - Role ID
   * @param {string} versionId - Version ID to restore
   * @returns {Promise<object>} Draft data loaded from the version
   */
  async restoreVersion(rolId, versionId) {
    return apiClient.post(`/api/roles/${rolId}/versions/${versionId}/restore`);
  }

  /**
   * Delete a role permanently (requires archive first + name confirmation)
   * @param {string} id - Role ID
   * @param {string} confirmationName - Must match the role name (case-insensitive)
   * @returns {Promise<object>}
   */
  async deleteRol(id, confirmationName) {
    return apiClient.delete(`/api/roles/${id}`, { body: { confirmationName } });
  }

  /**
   * Plans that reference this agent (#458) and the private memory deleted with it (#965),
   * to warn before deleting it.
   * @param {string} id - Role ID
   * @returns {Promise<{plans: Array<{id: string, name: string, steps: Array<{stepId: string, stepLabel: string}>}>, total: number, memory: {privateTypes: number, entries: number}}>}
   */
  async getRolDeleteImpact(id) {
    return apiClient.get(`/api/roles/${id}/delete-impact`);
  }

  /**
   * Archive a role (soft delete, reversible)
   * @param {string} id - Role ID
   * @returns {Promise<object>}
   */
  async archiveRol(id) {
    return apiClient.patch(`/api/roles/${id}/archive`);
  }

  /**
   * Restore an archived role
   * @param {string} id - Role ID
   * @returns {Promise<object>}
   */
  async restoreRol(id) {
    return apiClient.patch(`/api/roles/${id}/restore`);
  }

  /**
   * Mark a role as seen: it stops showing as «Nuevo» (#759 D20). Idempotent.
   * @param {string} id - Role ID
   * @returns {Promise<object>}
   */
  async markRolSeen(id) {
    return apiClient.post(`/api/roles/${id}/seen`);
  }

  /**
   * Get all archived roles for the current user
   * @returns {Promise<Array>}
   */
  async getArchivedRoles() {
    return apiClient.get('/api/roles/archived');
  }

  // ===== PLAN OPERATIONS =====

  /**
   * Get the caller's plans.
   * El ámbito lo pone el JWT en el backend (#576), que IGNORA cualquier `?userId=`. Los
   * callers que aún pasan un userId no rompen —JS descarta el argumento sobrante— pero
   * ese id ya no viaja en la URL, ni debe: sugeriría que el cliente puede elegir de quién
   * son los planes que pide.
   * @returns {Promise<Array>} List of plans
   */
  async getPlans() {
    return apiClient.get('/api/plans');
  }

  /**
   * Get a plan by ID
   * @param {string} id - Plan ID
   * @returns {Promise<object>} Plan data
   */
  async getPlanById(id) {
    return apiClient.get(`/api/plans/${id}`);
  }

  /**
   * Search among the caller's plans.
   * Mismo criterio que getPlans: el ámbito lo pone el JWT, no el query (#576).
   * @param {string} searchTerm - Search query
   * @returns {Promise<Array>} List of matching plans
   */
  async searchPlans(searchTerm) {
    const params = new URLSearchParams();
    params.append('q', searchTerm);
    return apiClient.get(`/api/plans/search?${params.toString()}`);
  }

  /**
   * Get plans by user ID
   * @param {string} userId - User ID
   * @returns {Promise<Array>} List of plans
   */
  async getPlansByUser(userId) {
    return apiClient.get(`/api/users/${userId}/plans`);
  }

  /**
   * Get roles involved in a plan
   * @param {string} planId - Plan ID
   * @returns {Promise<Array>} List of roles
   */
  async getPlanRoles(planId) {
    return apiClient.get(`/api/plans/${planId}/roles`);
  }

  /**
   * Create a new plan
   * @param {object} planData - Plan data
   * @returns {Promise<object>} Created plan
   */
  async createPlan(planData) {
    return apiClient.post('/api/plans', planData);
  }

  /**
   * Update an existing plan
   * @param {string} id - Plan ID
   * @param {object} planData - Updated plan data
   * @returns {Promise<object>} Updated plan
   */
  async updatePlan(id, planData) {
    return apiClient.put(`/api/plans/${id}`, planData);
  }

  /**
   * Delete a plan
   * @param {string} id - Plan ID
   * @returns {Promise<null>}
   */
  async deletePlan(id) {
    return apiClient.delete(`/api/plans/${id}`);
  }

  // ===== FILE OPERATIONS (ROLE FILES / KNOWLEDGE BASE) =====

  /**
   * Upload files for a role's knowledge base
   * @param {string} rolId - Role ID
   * @param {File[]} files - Files to upload
   * @returns {Promise<Array>} Uploaded files metadata
   */
  async uploadRolFiles(rolId, files) {
    if (!files || files.length === 0) {
      return [];
    }

    const formData = new FormData();
    files.forEach(file => formData.append('files', file));

    // apiClient.post handles FormData correctly (browser sets Content-Type with boundary)
    return apiClient.post(`/api/roles/${rolId}/files`, formData);
  }

  /**
   * Delete a file from role's knowledge base
   * @param {string} rolId - Role ID
   * @param {string} fileId - File ID
   * @returns {Promise<null>}
   */
  async deleteRolFile(rolId, fileId) {
    return apiClient.delete(`/api/roles/${rolId}/files/${fileId}`);
  }

  // ===== KNOWLEDGE SYNC (NEW ARCHITECTURE) =====

  /**
   * Trigger knowledge sync for role's files
   * Uses new rol_knowledge_collection and rol_knowledge_vector tables
   * @param {string} rolId - Role ID
   * @param {Object} [options] - Options (passed as the POST body verbatim)
   * @param {boolean} [options.retryErrors=false] - Also retry files with error status
   * @param {boolean} [options.retryWarnings=false] - Also retry files completed with
   *   non-fatal image warnings (#273)
   * @param {string} [options.fileId] - Scope the retry to a single file (#88)
   * @returns {Promise<Object>} Sync result
   */
  async triggerKnowledgeSync(rolId, options = {}) {
    return apiClient.post(`/api/roles/${rolId}/knowledge/sync`, options);
  }

  async retrySharepointFailed(collectionId) {
    return apiClient.post(`/api/rag-jobs/collections/${collectionId}/retry-sp-failed`);
  }

  async retrySharepointWarning(collectionId) {
    return apiClient.post(`/api/rag-jobs/collections/${collectionId}/retry-sp-warnings`);
  }

  /**
   * Get knowledge status for role
   * @param {string} rolId - Role ID
   * @returns {Promise<Object>} Knowledge status with collection and file stats
   */
  async getKnowledgeStatus(rolId) {
    return apiClient.get(`/api/roles/${rolId}/knowledge/status`);
  }

  /**
   * Get knowledge collection for role
   * @param {string} rolId - Role ID
   * @returns {Promise<Object|null>} Knowledge collection or null
   */
  async getKnowledgeCollection(rolId) {
    return apiClient.get(`/api/roles/${rolId}/knowledge`);
  }

  /**
   * Delete knowledge collection for role
   * Note: Usually automatic via CASCADE when role is deleted
   * @param {string} rolId - Role ID
   * @returns {Promise<Object>} Result
   */
  async deleteKnowledgeCollection(rolId) {
    return apiClient.delete(`/api/roles/${rolId}/knowledge`);
  }

  // ===== TEMPLATE OPERATIONS =====

  /**
   * Get all available templates for the current user
   * @returns {Promise<Array>} List of templates with role/tool counts
   */
  async getAvailableTemplates() {
    return apiClient.get('/api/templates');
  }

  /**
   * Get template details by ID (includes roles and tools)
   * @param {string} templateId - Template ID
   * @returns {Promise<object>} Template with roles and tools
   */
  async getTemplateById(templateId) {
    return apiClient.get(`/api/templates/${templateId}`);
  }

  /**
   * Get the active template for the current user
   * @returns {Promise<object|null>} Active template or null
   */
  async getActiveTemplate() {
    return apiClient.get('/api/templates/active');
  }

  /**
   * Assign a template to the current user
   * Deactivates previous template and copies all roles
   * @param {string} templateId - Template ID to assign
   * @returns {Promise<object>} Result with created roles
   */
  async assignTemplate(templateId) {
    return apiClient.post(`/api/templates/${templateId}/assign`);
  }

  /**
   * Get roles with updates available from their template
   * @returns {Promise<Array>} List of roles with pending updates
   */
  async getRolesWithUpdates() {
    return apiClient.get('/api/templates/updates-available');
  }

  /**
   * Sync non-customized roles with their template origin
   * @param {boolean} includeTools - Also sync tools (default: true)
   * @returns {Promise<Array>} List of synced roles
   */
  async syncRolesFromTemplate(includeTools = true) {
    return apiClient.post('/api/templates/sync', { includeTools });
  }

  /**
   * Restore a role to its original template definition
   * @param {string} rolId - Role ID to restore
   * @returns {Promise<object>} Restored role
   */
  async restoreRolToOriginal(rolId) {
    return apiClient.post(`/api/templates/roles/${rolId}/restore`);
  }

  /**
   * Get template statistics (admin)
   * @returns {Promise<Array>} Template statistics
   */
  async getTemplateStats() {
    return apiClient.get('/api/templates/admin/stats');
  }

  // ===== REGISTRATION CODE OPERATIONS =====

  /**
   * Validate a registration code (PUBLIC - no authentication)
   * @param {string} code - Code to validate
   * @returns {Promise<object>} Validation result with template info
   */
  async validateRegistrationCode(code) {
    // This is a public endpoint - use apiClient without auth
    return apiClient.post('/api/registration-codes/validate', { code }, { authenticated: false });
  }

  /**
   * Redeem a registration code (FL-*, BASIC2025, etc.) or org invite (inv_*).
   * Uses the canonical /api/access/redeem endpoint.
   * Callers don't need to parse the response — throw-or-success semantics.
   * @param {string} code - Code or invite token
   * @returns {Promise<object>} { kind: 'code'|'invite', ...details }
   */
  async useRegistrationCode(code) {
    return apiClient.post('/api/access/redeem', { token: code });
  }

  /**
   * Rollback a signup whose useCode step failed.
   * Deletes the Supabase account + user_profile for the authenticated user.
   * Only allowed when the user has no active template (incomplete signup).
   * @returns {Promise<object>} Success confirmation
   */
  async rollbackSignup() {
    return apiClient.delete('/api/auth/rollback-signup');
  }

  // ===== ADMIN: REGISTRATION CODES =====

  /**
   * Get all registration codes (admin)
   * @param {object} options - Filter options
   * @param {boolean} options.activeOnly - Only active codes
   * @returns {Promise<Array>} List of codes
   */
  async getAllRegistrationCodes(options = {}) {
    const params = new URLSearchParams();
    if (options.activeOnly) params.append('activeOnly', 'true');
    const query = params.toString();
    return apiClient.get(`/api/registration-codes${query ? `?${query}` : ''}`);
  }

  /**
   * Create a new registration code (admin)
   * @param {object} data - Code data
   * @returns {Promise<object>} Created code
   */
  async createRegistrationCode(data) {
    return apiClient.post('/api/registration-codes', data);
  }

  /**
   * Update a registration code (admin)
   * @param {string} codeId - Code ID
   * @param {object} data - Fields to update
   * @returns {Promise<object>} Updated code
   */
  async updateRegistrationCode(codeId, data) {
    return apiClient.put(`/api/registration-codes/${codeId}`, data);
  }

  /**
   * Deactivate a registration code (admin)
   * @param {string} codeId - Code ID
   * @returns {Promise<null>}
   */
  async deactivateRegistrationCode(codeId) {
    return apiClient.delete(`/api/registration-codes/${codeId}`);
  }

  /**
   * Get usage statistics for a code (admin)
   * @param {string} codeId - Code ID
   * @returns {Promise<object>} Code statistics
   */
  async getRegistrationCodeStats(codeId) {
    return apiClient.get(`/api/registration-codes/${codeId}/stats`);
  }
}

// Export singleton instance
export default new PostgresBackendService();
